import {
  VTextField,
  VTextarea,
  VSelect,
  VAutocomplete,
  VSwitch,
} from 'vuetify/components';

import GroupedFieldsExpansionPanel from '@/components/form/GroupedFieldsExpansionPanel.vue'
import ClientDetail from '@/components/modules/clients/Detail.vue'
import DynamicJsonField from '@/components/form/DynamicJsonField.vue'
import FileUploader from '@/components/form/FileUploader.vue';

import type { FormField } from '@/types/form';

function getHostnameSafe(): string {
  // Navegador
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  // SSR / Node (si lo necesitas, puedes leer una var de entorno)
  return process.env.VITE_PUBLIC_HOST ?? process.env.SSR_HOST ?? '';
}

// Usa comparación estricta; ajusta si necesitas subdominios.
const IS_DOMINIOMEDIOS = getHostnameSafe() === 'crm.dominiomedios.com';

const AGENCIES_FIELD: FormField[] = [
  { value: 'origin', 
    label: 'Procedencia', 
    component: VSelect, 
    searchValue: 'name', 
    required: false, 
    col: 6, 
    fetchOptions: 'originsFromStore', 
    itemText: 'name', 
    itemValue: 'name'
  },
  { value: 'prefered_contact_type', label: 'Medio de Contacto', component: VTextField, required: false, col: 6 },
  { value: 'credit_days', label: 'Días de Crédito', component: VTextField, required: false, col: 6 },
  { value: 'credit_limit', label: 'Limite de Crédito', component: VTextField, required: false, col: 6 },
  { value: 'special_note', label: 'Nota Especial', component: VTextarea, required: false, col: 12 },
  
];

export const CREATE_FORM: FormField[] = [  
  { value: 'client_type', 
    label: 'Tipo de Cliente', 
    component: VAutocomplete, 
    searchValue: 'name', 
    required: true, 
    col: 6, 
    mdcol:12, 
    fetchOptions: 'getClientTypes', 
    itemText: 'name', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      client_type_id: value?.id,
    }) 
  },
  { value: 'agencies',
    label: 'Agencia(s)',
    component: VAutocomplete,
    searchValue: 'name',
    required: false,
    col: 6,
    mdcol: 12,
    fetchOptions: 'searchAgencies',
    itemText: (item: any) =>
      item?.attributes?.commercial_name ||
      item?.attributes?.legal_name ||
      item?.attributes?.name || '',
    itemValue: (item: any) => item,
    multiple: true,

    dependsOn: 'client_type',
    dependsBehavior: 'hidden',
    dependsCondition: { value: 'agencia', condition: 'includes' },

    transform: (value: any) => ({
      agency_ids: Array.isArray(value)
        ? value.map(v => v?.id ?? v)
        : value?.id ?? value
    }),
    fetchParams: () => ({ 'filter[tag]': 'Agencia' }),
  },
  { value: 'commercial_name', label: 'Nombre Comercial', component: VTextField, required: false, col: 6, mdcol:12 },
  { value: 'phone', label: 'Teléfono', component: VTextField, required: false, col: 6 },
  { value: 'email', label: 'Email', component: VTextField, required: false, col: 6 },
  ...(!IS_DOMINIOMEDIOS ? AGENCIES_FIELD : []),
  {
    value: 'fiscal_data',
    label: 'Datos Fiscales',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    col: 12,
    children: [
      { value: 'legal_name', label: 'Razón Social', component: VTextField, required: false, col: 6 },
      { value: 'tax_id', label: 'RFC', component: VTextField, required: false, col: 6 },
      { value: 'tax_system', label: 'Régimen Fiscal', component: VAutocomplete, searchValue: 'name', required: false, col: 6, fetchOptions: 'searchFiscalRegiments', itemText: 'label',
        itemValue: (item: any) => item,
        transform: (value: any) => ({
          tax_system: value?.value,
        })
      },
      { value: 'preferred_payment_method', label: 'Método de Pago', component: VAutocomplete, searchValue: 'name', required: false, col: 6, fetchOptions: 'searchPaymentMethods', itemText: 'label',
        itemValue: (item: any) => item,
        transform: (value: any) => ({
          preferred_payment_method: value?.value,
        })
      },
    ],
  },
  {
    value: 'address',
    label: 'Dirección',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    col: 12,
    children: [
      { value: 'street', label: 'Calle', component: VTextField, required: false, col: 6 },
      { value: 'exterior', label: '# Exterior', component: VTextField, required: false, col: 6 },
      { value: 'interior', label: '# Interior', component: VTextField, required: false, col: 6 },
      { value: 'neighborhood', label: 'Colonia', component: VTextField, required: false, col: 6 },
      { value: 'zip', label: 'Código Postal', component: VTextField, required: false, col: 6 },
      { value: 'city', label: 'Ciudad', component: VTextField, required: false, col: 6 },
      { value: 'municipality', label: 'Municipio', component: VTextField, required: false, col: 6 },
      { value: 'state', label: 'Estado', component: VTextField, required: false, col: 6 },
      { value: 'country', label: 'País', component: VTextField, required: false, col: 6 }, 
    ],
  },
  {
    value: 'documents_panel',
    label: 'Documentos',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    layoutOnly: true,
    col: 12,
    children: [
      {
        value: 'additional_data.documents',
        label: 'Documentos',
        isJsonArray: true,
        plainJson: false,
        component: DynamicJsonField,
        col: 12,
        children: [
          {
            value: 'label',
            label: 'Etiqueta',
            component: VTextField,
            col: 6,
            required: false,
          },
          {
            value: 'file',
            label: 'Archivo',
            component: FileUploader,
            module: 'clients',
            col: 6,
            required: false,
          }
        ],
      }
    ]
  }
];

const idFieldMappings: Record<string, string> = {
  agency_ids:'agencies',
  client_type_id:'client_type'
};

export const EDIT_FORM: FormField[] = [...CREATE_FORM.map(field => {
  if (field.value === 'fiscal_data') {
    return {
      ...field,
      children: field.children?.map(child =>
        child.value === 'legal_name'
          ? { ...child, required: true }
          : child
      )
    };
  }

  const relatedKey = idFieldMappings[field.value];
  if (relatedKey) {
    return {
      ...field,
      parse: (_: any, item: any) => item[relatedKey] || null,
      transform: (val: any) => ({
        [field.value]: field.multiple
          ? (Array.isArray(val)
              ? val.map(v => v?.[field.itemValue || 'id'] ?? v)
              : [])
          : val?.id ?? val
      })
    };
  }
  return field;
})]

export const HEADERS = [
  { title: '', value: 'eye_detail', format: 'text', sortable: false, fixed: true },
  { title: 'ID', value: 'id', format: 'text', sortable: true },
  { title: 'Razón Social', value: 'attributes.legal_name', format: 'text', sortable: false },
  { title: 'Nombre Comercial', value: 'attributes.commercial_name', format: 'text', sortable: false },
  { title: 'RFC', value: 'rfc', format: 'attributes.text', sortable: true },
  { title: 'Régimen Fiscal', value: 'attributes.tax_system', format: 'text', sortable: true },
  { title: 'Dirección', value: 'attributes.address', format: 'text', sortable: false },
  { title: 'Correo', value: 'attributes.email', format: 'text', sortable: true },
  { title: 'Teléfono', value: 'attributes.phone', format: 'text', sortable: true },
  { title: 'Método de Pago', value: 'attributes.preferred_payment_method', format: 'text', sortable: false },
  { title: 'Tipo de Cliente', value: 'attributes.client_type', format: 'text', sortable: false },
  { title: 'Tipo', value: 'type', format: 'text', sortable: false },
  ...(IS_DOMINIOMEDIOS ? [{ title: 'Agencia', value: 'attributes.belongs_to_agency', format: 'boolean', sortable: true }] : []),
  
  { title: 'Contacto Principal', value: 'attributes.primary_contact', format: 'text', sortable: false },
  ...(IS_DOMINIOMEDIOS ? [{ title: 'Cuentas', value: 'attributes.accounts', format: 'text', sortable: false }] : []),
  ...(IS_DOMINIOMEDIOS ? [{ title: 'Agencias', value: 'attributes.agencies', format: 'text', sortable: false }] : []),
  { title: 'Vendedor', value: 'user', format: 'text', sortable: true },
];

export const DEFAULT_HEADER_ORDER = [
  'eye_detail',
  'id',
  'attributes.commercial_name',
  'attributes.client_type',
  'attributes.primary_contact',
  IS_DOMINIOMEDIOS ? 'attributes.accounts':'',
  IS_DOMINIOMEDIOS ? 'attributes.agencies':''
];

export const DETAIL = {
  expand_table: false,
  eye_detail: true,
  key: 'all',
  component: ClientDetail
};

export const ROW_ACTIONS = [
  ...(IS_DOMINIOMEDIOS ? [{ label: 'Enviar Media Kit', onClick: 'sendMediaKit', permission:undefined }] : []),
  { label: 'Editar', onClick: '__edit', permission:'clients.update' },
  { label: 'Eliminar', onClick: '__delete', permission:'clients.delete' },
];

export const FILTERS = [
  { value: 'id', label: 'ID', component: VTextField },
  { value: 'name', label: 'Nombre', component: VTextField },
  { value: 'commercial_name', label: 'Nombre Comercial', component: VTextField },
  { value: 'legal_name', label: 'Razón Social', component: VTextField },
  //{ value: 'email', label: 'Correo Electrónico', component: VTextField },
  //{ value: 'phone', label: 'Teléfono', component: VTextField },
  { value: 'user_id', label: 'Vendedor', component: VAutocomplete, fetchOptions: 'searchUsers', 
    searchValue: 'name',
    itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '',
    itemValue: (item: any) => item,
    transform: (values: any) => ({
      user_id: Array.isArray(values)
      ?values.map(v=>v.id??v).join(',')
      :values?.id ?? values
    }),
    multiple:true
  },
  { value: 'tax_id', label: 'RFC', component: VTextField },
  { value: 'tax_system', label: 'Régimen Fiscal', component: VAutocomplete, searchValue: 'name', fetchOptions: 'searchFiscalRegiments', itemText: 'label', itemValue: 'value'},
  { value: 'client_type', label: 'Tipo de Cliente', component: VAutocomplete, fetchOptions: 'getClientTypes',
    itemText: 'name', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      client_type_id: value?.id,
    }) 
  },
];
